// The EXECUTE bar — the card's ONE primary button (brief §5.3.2), pinned under the decision blocks
// with a LIVE plain-English summary of what is staged. Disabled until at least one verdict is
// staged, disabled with an honest inline explanation while a linked proposal is staged
// incoherently, and busy-disabled while the ONE commit routes (a double-click cannot double-route).
// Refusal/conflict notes arrive SANITISED from the transport and render as a status line (G-197 —
// no internal detail). This component renders staging truth only; the commit itself is the
// panel's route-then-re-read (never optimistic — G-182).

import type { StagedSummary, StagingInconsistency } from './staging.ts';

const PRIMARY_BTN =
  'rounded-sm bg-action px-3 py-1.5 text-sm font-medium text-action-text hover:bg-action-hover disabled:opacity-60';

export interface ExecuteBarProps {
  readonly summary: StagedSummary;
  readonly inconsistency: StagingInconsistency | null;
  readonly busy: boolean;
  /** The sanitised commit-level note from a refused/conflicted Execute; null when quiet. */
  readonly note: string | null;
  readonly onExecute: () => void;
}

/** The honest bar notes: the incoherent-staging explanation and the sanitised refusal note —
 *  BOTH live status regions, so AT hears why Execute is held the moment the note appears. */
function BarNotes({
  inconsistency,
  note,
}: {
  readonly inconsistency: StagingInconsistency | null;
  readonly note: string | null;
}): React.JSX.Element {
  return (
    <>
      {inconsistency !== null ? (
        <p role="status" data-inconsistent="true" className="mt-2 text-sm text-bracken">
          Staging is inconsistent: “{inconsistency.dependentTitle}” depends on “
          {inconsistency.dependencyTitle}” — stage that approve too, or clear this tick.
        </p>
      ) : null}
      {note !== null ? (
        <p role="status" className="mt-2 text-sm text-bracken">
          {note}
        </p>
      ) : null}
    </>
  );
}

/** The staged-verdict summary + the one Execute button (brief §3.5 step 2). */
export function ExecuteBar({
  summary,
  inconsistency,
  busy,
  note,
  onExecute,
}: ExecuteBarProps): React.JSX.Element {
  const stagedCount = summary.approves + summary.rejects;
  const disabled = busy || stagedCount === 0 || inconsistency !== null;
  return (
    <div
      data-staged-approves={String(summary.approves)}
      data-staged-rejects={String(summary.rejects)}
      className="sticky bottom-0 z-10 mt-4 rounded-md border border-border bg-surface px-5 py-3"
    >
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-text-muted">
          {stagedCount === 0 ? (
            'Nothing staged yet — tick a verdict on each proposal you want to commit.'
          ) : (
            <>
              You’re about to: <span className="font-medium text-text">{summary.text}</span>
            </>
          )}
        </p>
        <button
          type="button"
          disabled={disabled}
          onClick={onExecute}
          aria-label="Execute staged verdicts"
          className={PRIMARY_BTN}
        >
          {busy ? 'Executing…' : 'Execute'}
        </button>
      </div>
      <BarNotes inconsistency={inconsistency} note={note} />
    </div>
  );
}
